/**
 * InvestorDeliveryTab Component
 *
 * Tab content for tracking investor delivery information:
 * - Buy price tracking (expected vs actual with pickup/loss)
 * - Delivery events (shipping dates, tracking, synced to Encompass)
 * - Investor conditions (copy/paste from purchase advice for pattern analysis)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSyncedDraftField } from '@/hooks/useSyncedDraftField';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useWarehouseLine } from '@/hooks/useWarehouseLine';
import { WarehouseLineSelect } from './WarehouseLineSelect';
import { PrimisCollateralDate } from './PrimisCollateralDate';
import { usePrimisCollateral } from '@/hooks/usePrimisCollateral';
import { matchWarehouseLine } from '../../../../supabase/functions/_shared/warehouse-line-contract';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter
} from '@/components/ui/table';
import {
  DollarSign,
  Save,
  Loader2,
  ExternalLink,
  Package,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Upload,
  Eye,
  MessageSquare,
  Clock,
  XCircle,
  type LucideIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { bankerReconService } from '@/services/bankerReconService';
import { uploadPurchaseAdvice } from '@/services/purchaseAdviceUploadService';
import { getDocumentDownloadUrl } from '@/services/loanDocumentService';
import {
  addPostClosingComment,
  getPostClosingComments,
  getPostClosingConditionDocuments,
  sendPostClosingLoanPurchasedEmail,
  uploadPostClosingCondition,
  type PostClosingComment,
  type PostClosingConditionDocument
} from '@/services/postClosingWorkflowService';
import { useEncompassFieldSync } from '@/hooks/useEncompassFieldSync';
import { SyncStatusIcon } from '@/components/encompass/SyncStatusBadge';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import {
  getPricingAdjustments,
  getDisplayCategory,
  bpsToDollars,
  formatCurrency,
  formatPoints,
  type LoanPricingAdjustment
} from '@/services/loanPricingService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { isExtendedUser } from '@/types/auth';
import { PostClosingProgress } from './PostClosingProgress';
import type { ProspectLoan } from './types';
import type { BankerRecon } from '@/types/banker-recon';
import { createComponentLogger } from '@/utils/logger';
import {
  matchesClarifiiInvestor,
  composeClarifiiRelianceEmail,
  normalizeInvestorName,
  type ClarifiiInvestorConfigMap,
  type ClarifiiRelianceEmail
} from './clarifiiHelpers';

const log = createComponentLogger('InvestorDeliveryTab', 'encompass-prospects');

/**
 * Format a date string to a readable format
 */
function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) {
return '-';
}
  try {
    const date = parseAsLocalDate(dateStr);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr: string | undefined | null): string {
  if (!dateStr) {
return '-';
}
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return dateStr;
  }
}

function compactCommentPreview(content: string): string {
  return content.replace(/\s+/g, ' ').trim().slice(0, 160);
}

function getConditionStatus(doc: PostClosingConditionDocument): string {
  return doc.queue_status || doc.loan_document_status || 'queued';
}

function getConditionError(doc: PostClosingConditionDocument): string | null {
  return doc.error_message || doc.fallback_error_message || null;
}

function conditionStatusView(status: string): { label: string; className: string; icon: LucideIcon } {
  if (status === 'completed') {
    return {
      label: 'Completed',
      className: 'bg-green-50 text-green-700 border-green-200',
      icon: CheckCircle2
    };
  }
  if (status === 'failed' || status === 'error') {
    return {
      label: 'Failed',
      className: 'bg-red-50 text-red-700 border-red-200',
      icon: XCircle
    };
  }
  if (status === 'processing') {
    return {
      label: 'Processing',
      className: 'bg-blue-50 text-blue-700 border-blue-200',
      icon: Loader2
    };
  }
  return {
    label: 'Queued',
    className: 'bg-orange-50 text-orange-700 border-orange-200',
    icon: Clock
  };
}

interface InvestorDeliveryTabProps {
  loan: ProspectLoan;
  onLoanUpdate?: (updates: Partial<ProspectLoan>) => void;
}

export function InvestorDeliveryTab({ loan, onLoanUpdate }: InvestorDeliveryTabProps) {
  const { user } = useAuth();
  const { enabled: warehouseEnabled } = useFeatureFlag('warehouse_line_v1');
  const warehouse = useWarehouseLine(loan, warehouseEnabled);
  const collateral = usePrimisCollateral(loan, warehouseEnabled, onLoanUpdate);
  const isPrimis = warehouseEnabled && matchWarehouseLine(warehouse.draftValue) === 'Primis Bank';

  // Loading state
  const [loading, setLoading] = useState(true);
  const [bankerRecon, setBankerRecon] = useState<BankerRecon | null>(null);
  const [pricingAdjustments, setPricingAdjustments] = useState<LoanPricingAdjustment[]>([]);

  // Investor conditions tracking
  interface TrackingRecord {
    investor_conditions_status: 'none' | 'outstanding' | 'cleared';
    conditions_outstanding_at: string | null;
    conditions_cleared_at: string | null;
    updated_by_name: string | null;
  }
  const [tracking, setTracking] = useState<TrackingRecord | null>(null);
  const [savingConditions, setSavingConditions] = useState(false);

  // PA document state
  interface PADoc {
    id: string;
    source_file: string;
    created_at: string;
    uploaded_by: string | null;
  }
  const [paDoc, setPaDoc] = useState<PADoc | null>(null);
  const [uploadingPA, setUploadingPA] = useState(false);
  const paInputRef = useRef<HTMLInputElement>(null);

  // Post Closing comments and condition uploads
  const [postClosingComments, setPostClosingComments] = useState<PostClosingComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [conditionDocs, setConditionDocs] = useState<PostClosingConditionDocument[]>([]);
  const [uploadingCondition, setUploadingCondition] = useState(false);
  const conditionInputRef = useRef<HTMLInputElement>(null);

  // Clarifi Cleared state
  const clarifiClearedDate = useSyncedDraftField(loan.clarifi_cleared_date || '');
  const [savingClarifiCleared, setSavingClarifiCleared] = useState(false);
  const [clarifiInvestorConfigs, setClarifiInvestorConfigs] = useState<ClarifiiInvestorConfigMap>(new Map());
  const [clarifiConfirmOpen, setClarifiConfirmOpen] = useState(false);
  const [pendingClarifiEmail, setPendingClarifiEmail] = useState<ClarifiiRelianceEmail | null>(null);
  const [sendingClarifi, setSendingClarifi] = useState(false);

  // Shipping/Delivery Events — dirty-safe draft state
  const shipDate = useSyncedDraftField(loan.shipping_actual_ship_date || '');
  const trackingNumber = useSyncedDraftField(loan.shipping_tracking_number || '');
  const investorDeliveryDate = useSyncedDraftField(loan.shipping_investor_delivery_date || '');
  const [isSavingShipping, setIsSavingShipping] = useState(false);
  const shippingFieldsDirty = shipDate.isDirty || trackingNumber.isDirty || investorDeliveryDate.isDirty;
  const warehouseDirty = warehouseEnabled && warehouse.isDirty;
  const warehouseReadyToSave = warehouseDirty && !warehouse.blocked;
  const collateralDirty = isPrimis && collateral.isDirty;
  const collateralReadyToSave = collateralDirty && !collateral.blocked && collateral.valid;
  const shippingDirty = shippingFieldsDirty || warehouseDirty || collateralDirty;

  // Ready to Ship — dirty-safe draft state
  const readyToShipDate = useSyncedDraftField(loan.ready_to_ship_date || '');
  const [savingReadyToShip, setSavingReadyToShip] = useState(false);

  // Durable Encompass field sync hook
  const { enqueueWrite, getSyncStatus } = useEncompassFieldSync(loan.id, loan.encompass_loan_id);
  const shipDateSyncStatus = getSyncStatus('2014');
  const trackingNumberSyncStatus = getSyncStatus('2018');
  const investorDeliverySyncStatus = getSyncStatus('2012');

  // Get loan amount for dollar calculations
  const loanAmount = loan.rate_lock_base_loan_amount || loan.trans_details_loan_amt || 0;

  // Get margin adjustments from Encompass (ProfitMargin type)
  const marginAdjustments = useMemo(() => {
    return (loan.rate_lock_buy_side_adjustments || [])
      .filter(adj => adj.priceAdjustmentType === 'ProfitMargin');
  }, [loan.rate_lock_buy_side_adjustments]);

  // Calculate Investor Lock Price (Encompass Buy Price + Margin)
  // Note: Encompass stores margins as NEGATIVE values, so we use Math.abs()
  const { investorLockPrice, totalMarginPoints, totalMarginDollars, pointsAbovePar, grossRevenueDollars } = useMemo(() => {
    const encompassBuyPrice = loan.rate_lock_total_buy_price || 0;

    // Sum margin points (convert negative to positive)
    const marginPoints = marginAdjustments.reduce(
      (sum, adj) => sum + Math.abs(adj.rate),
      0
    );

    // Calculate dollar value using shared utility
    const marginDollars = bpsToDollars(marginPoints * 100, loanAmount);

    // Calculate investor lock price and revenue
    const lockPrice = encompassBuyPrice + marginPoints;
    const abovePar = lockPrice - 100;
    const grossRevenue = (abovePar / 100) * loanAmount;

    return {
      investorLockPrice: lockPrice,
      totalMarginPoints: marginPoints,
      totalMarginDollars: marginDollars,
      pointsAbovePar: abovePar,
      grossRevenueDollars: grossRevenue
    };
  }, [loan.rate_lock_total_buy_price, marginAdjustments, loanAmount]);

  const refreshConditionDocs = useCallback(async () => {
    const docs = await getPostClosingConditionDocuments(loan.encompass_loan_id);
    setConditionDocs(docs);
    return docs;
  }, [loan.encompass_loan_id]);

  // Load banker recon, pricing adjustments, tracking, comments, and documents
  const fetchData = useCallback(async () => {
    if (!loan.organization_id) {
return;
}

    try {
      setLoading(true);

      const [recon, adjustments, trackingResult, paDocResult, commentsResult, conditionDocsResult] = await Promise.all([
        bankerReconService.getBankerReconByLoan(
          loan.organization_id,
          loan.encompass_loan_id
        ),
        getPricingAdjustments(loan.encompass_loan_id),
        supabase
          .from('loan_post_closing_tracking')
          .select('investor_conditions_status, conditions_outstanding_at, conditions_cleared_at, updated_by_name')
          .eq('organization_id', loan.organization_id)
          .eq('encompass_loan_id', loan.encompass_loan_id)
          .maybeSingle()
          .then(({ data, error }) => {
            if (error) {
              log.error('Failed to fetch post-closing tracking', { encompassLoanId: loan.encompass_loan_id }, error as unknown as Error);
              return null;
            }
            return data as TrackingRecord | null;
          }),
        supabase
          .from('loan_documents')
          .select('id, source_file, created_at, uploaded_by')
          .eq('encompass_loan_id', loan.encompass_loan_id)
          .eq('organization_id', loan.organization_id)
          .eq('document_type', 'purchase_advice')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
          .then(({ data, error }) => {
            if (error) {
              log.error('Failed to fetch PA document', { encompassLoanId: loan.encompass_loan_id }, error as unknown as Error);
              return null;
            }
            return data as PADoc | null;
          }),
        getPostClosingComments(loan.encompass_loan_id).catch((error) => {
          log.error('Failed to fetch Post Closing comments', { encompassLoanId: loan.encompass_loan_id }, error as Error);
          return [] as PostClosingComment[];
        }),
        getPostClosingConditionDocuments(loan.encompass_loan_id).catch((error) => {
          log.error('Failed to fetch Post Closing condition documents', { encompassLoanId: loan.encompass_loan_id }, error as Error);
          return [] as PostClosingConditionDocument[];
        })
      ]);

      setBankerRecon(recon);
      setPricingAdjustments(adjustments);
      setTracking(trackingResult);
      setPaDoc(paDocResult);
      setPostClosingComments(commentsResult);
      setConditionDocs(conditionDocsResult);

    } catch (error) {
      log.error('Failed to fetch delivery data', { encompassLoanId: loan.encompass_loan_id }, error as Error);
    } finally {
      setLoading(false);
    }
  }, [loan.organization_id, loan.encompass_loan_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load Clarifii investor configs
  useEffect(() => {
    if (!loan.organization_id) {
return;
}
    supabase.from('investors')
      .select('investor_name, clarifi_reliance_investor_recipients')
      .eq('organization_id', loan.organization_id)
      .eq('clarifi_reliance_required', true)
      .is('deleted_at', null)
      .then(({ data, error }) => {
        if (error) {
          log.error('Failed to load Clarifii investor configs', {}, error as unknown as Error);
          return;
        }
        const map: ClarifiiInvestorConfigMap = new Map();
        data?.forEach(inv => {
          map.set(
            normalizeInvestorName(inv.investor_name),
            (inv.clarifi_reliance_investor_recipients as string[]) || []
          );
        });
        setClarifiInvestorConfigs(map);
      });
  }, [loan.organization_id]);

  const matchedClarifiiRecipients = matchesClarifiiInvestor(loan.investor_name, clarifiInvestorConfigs);
  const isClarifiiLoan = matchedClarifiiRecipients !== null;

  // Handle shipping field changes
  const handleShippingChange = useCallback((field: 'shipDate' | 'trackingNumber' | 'investorDeliveryDate', value: string) => {
    if (field === 'shipDate') {
shipDate.setValue(value);
} else if (field === 'trackingNumber') {
trackingNumber.setValue(value);
} else if (field === 'investorDeliveryDate') {
investorDeliveryDate.setValue(value);
}
  }, [shipDate, trackingNumber, investorDeliveryDate]);

  // Save all shipping fields to Encompass
  const handleSaveShipping = useCallback(async () => {
    if (!shippingDirty) {
return;
}

    // Separate dirty fields into saveable (non-blank) and unsupported clears (blank).
    const allDirty = [
      { draft: shipDate, fieldId: '2014', dbKey: 'shipping_actual_ship_date' },
      { draft: trackingNumber, fieldId: '2018', dbKey: 'shipping_tracking_number' },
      { draft: investorDeliveryDate, fieldId: '2012', dbKey: 'shipping_investor_delivery_date' }
    ].filter(f => f.draft.isDirty);

    const fieldsToSave = allDirty.filter(f => f.draft.value.trim());
    const blankClears = allDirty.filter(f => !f.draft.value.trim());

    // Reset blank clears with feedback — clearing populated shipping fields is not supported
    if (blankClears.length > 0) {
      blankClears.forEach(f => f.draft.reset());
      if (fieldsToSave.length === 0 && !warehouseReadyToSave && !collateralReadyToSave) {
        toast.error('Clearing shipping fields is not supported');
        return;
      }
      toast.warning('Blank fields were reverted — clearing is not supported');
    }

    if (fieldsToSave.length === 0 && !warehouseReadyToSave && !collateralReadyToSave) {
return;
}

    try {
      setIsSavingShipping(true);
      log.info('Saving shipping fields via durable queue', {
        loanId: loan.id,
        fields: fieldsToSave.map(f => f.fieldId),
        isFileLocked: loan.is_file_locked
      });

      const [results, warehouseAccepted, collateralSaved] = await Promise.all([
        Promise.all(fieldsToSave.map(f => enqueueWrite({ fieldId: f.fieldId, value: f.draft.value.trim() }))),
        warehouseReadyToSave ? warehouse.saveDraft() : Promise.resolve(true),
        collateralReadyToSave ? collateral.save() : Promise.resolve(true)
      ]);

      const successCount = results.filter(r => r.success).length + (warehouseReadyToSave && warehouseAccepted ? 1 : 0)
        + (collateralReadyToSave && collateralSaved ? 1 : 0);
      const fieldCount = fieldsToSave.length + (warehouseReadyToSave ? 1 : 0) + (collateralReadyToSave ? 1 : 0);

      // Mark each successful field clean individually and emit overrides
      const updates: Partial<Record<string, string>> = {};
      fieldsToSave.forEach((f, i) => {
        if (results[i].success) {
          f.draft.markClean();
          updates[f.dbKey] = f.draft.value.trim();
        }
      });
      if (Object.keys(updates).length > 0) {
        onLoanUpdate?.(updates as Partial<typeof loan>);
      }

      // User feedback
      if (successCount === fieldCount) {
        if (warehouseDirty && !warehouseReadyToSave) {
          toast.success('Other delivery events saved. Warehouse confirmation is still pending.');
        } else if (results.some(result => result.status === 'pending')) {
          toast.info(`Saved. ${loan.file_locked_by_name || 'Someone'} has this loan open - will sync to Encompass when they close it.`);
        } else if (warehouseReadyToSave) {
          toast.success('Delivery events saved');
        } else if (fieldsToSave.length > 0) {
          toast.success(collateralReadyToSave
            ? 'Delivery events synced to Encompass. Collateral request date saved.'
            : 'Delivery events synced to Encompass');
        } else {
          toast.success('Collateral request date saved');
        }
      } else if (successCount > 0) {
        toast.warning(`${successCount} of ${fieldCount} fields saved — check the remaining fields`);
      } else {
        const failedResult = results.find(r => !r.success);
        toast.error(failedResult?.error || 'Failed to save delivery events');
      }
    } catch (error) {
      log.error('Failed to save shipping fields', {}, error as Error);
      toast.error('Failed to save delivery events');
    } finally {
      setIsSavingShipping(false);
    }
  }, [loan.id, loan.is_file_locked, loan.file_locked_by_name, shipDate, trackingNumber, investorDeliveryDate, shippingDirty, onLoanUpdate, enqueueWrite, warehouseDirty, warehouseReadyToSave, warehouse, collateralReadyToSave, collateral]);

  // Save Ready to Ship date
  const handleSaveReadyToShip = useCallback(async () => {
    if (!readyToShipDate.isDirty || !user?.id) {
return;
}

    try {
      setSavingReadyToShip(true);

      const now = new Date().toISOString();
      const userName = (user as { fullName?: string }).fullName || user.email || 'Unknown';

      const { error } = await supabase.rpc('set_loan_ready_to_ship', {
        p_loan_id: loan.id,
        p_ready_to_ship_date: readyToShipDate.value || null
      });

      if (error) {
        log.error('Failed to save ready-to-ship date', { loanId: loan.id }, error as unknown as Error);
        toast.error('Failed to save ready-to-ship date');
        return;
      }

      toast.success('Ready to ship date saved');
      readyToShipDate.markClean();
      onLoanUpdate?.({
        ready_to_ship_date: readyToShipDate.value || undefined,
        ready_to_ship_set_by: user.id,
        ready_to_ship_set_by_name: userName,
        ready_to_ship_set_at: now
      });
    } catch (error) {
      log.error('Error saving ready-to-ship date', {}, error as Error);
      toast.error('Failed to save ready-to-ship date');
    } finally {
      setSavingReadyToShip(false);
    }
  }, [readyToShipDate, user, loan.id, onLoanUpdate]);

  // Check if investor delivery date meets lock expiration
  const lockMet = useMemo(() => {
    if (!investorDeliveryDate.value || !loan.rate_lock_buy_side_lock_expires) {
return false;
}
    const deliveryDate = parseAsLocalDate(investorDeliveryDate.value);
    const lockExpires = parseAsLocalDate(loan.rate_lock_buy_side_lock_expires);
    return deliveryDate <= lockExpires;
  }, [investorDeliveryDate.value, loan.rate_lock_buy_side_lock_expires]);

  // ── Clarifi Cleared handlers ────────────────────────────────────────────

  const handleSaveClarifiCleared = useCallback(async () => {
    if (!clarifiClearedDate.isDirty || !user?.id) {
return;
}

    try {
      setSavingClarifiCleared(true);
      const previousValue = loan.clarifi_cleared_date || '';
      const value = clarifiClearedDate.value;

      const userName = isExtendedUser(user)
        ? ((user as { fullName?: string }).fullName || user.email || 'Unknown')
        : (user.email || 'Unknown');

      const updatedAt = new Date().toISOString();
      const { error } = await supabase.rpc('set_loan_clarifi_cleared', {
        p_loan_id: loan.id,
        p_clarifi_cleared_date: value || null
      });

      if (error) {
        log.error('Failed to save Clarifi cleared date', { loanId: loan.id }, error as unknown as Error);
        toast.error('Failed to save Clarifi cleared date');
        return;
      }

      clarifiClearedDate.markClean();
      onLoanUpdate?.({
        clarifi_cleared_date: value || undefined,
        clarifi_cleared_set_by: user.id,
        clarifi_cleared_set_by_name: userName,
        clarifi_cleared_set_at: updatedAt
      });

      // Send-eligible: previous prop was blank AND new value is non-empty
      const isSendEligible = !previousValue && !!value;

      if (isSendEligible && matchedClarifiiRecipients && matchedClarifiiRecipients.length > 0
          && loan.trans_details_loan_number && loan.rate_lock_investor_commitment && loan.encompass_loan_id) {
        const email = composeClarifiiRelianceEmail(
          {
            trans_details_loan_number: loan.trans_details_loan_number,
            rate_lock_investor_commitment: loan.rate_lock_investor_commitment,
            borrower_first_name: loan.borrower_first_name,
            borrower_last_name: loan.borrower_last_name
          },
          matchedClarifiiRecipients
        );
        setPendingClarifiEmail(email);
        setClarifiConfirmOpen(true);
      } else if (isSendEligible && !loan.encompass_loan_id) {
        toast.warning('Missing loan identifier — send reliance letter manually.');
      } else if (isSendEligible) {
        toast.warning('Date saved. Missing data for reliance letter — send manually.');
      } else {
        toast.success(value ? 'Clarifi cleared date saved' : 'Clarifi cleared date removed');
      }
    } catch (err) {
      log.error('Error saving Clarifi cleared date', { loanId: loan.id }, err as Error);
      toast.error('Failed to save Clarifi cleared date');
    } finally {
      setSavingClarifiCleared(false);
    }
  }, [clarifiClearedDate, user, loan.id, loan.clarifi_cleared_date, loan.trans_details_loan_number, loan.rate_lock_investor_commitment, loan.encompass_loan_id, loan.borrower_first_name, loan.borrower_last_name, matchedClarifiiRecipients, onLoanUpdate]);

  const handleConfirmClarifiSend = useCallback(async () => {
    setSendingClarifi(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-clarifii-reliance', {
        body: { encompassLoanId: loan.encompass_loan_id }
      });

      if (error) {
        // 5xx / transport failure — no structured data available
        log.error('Clarifii reliance send failed (transport)', { loanId: loan.id }, error as Error);
        toast.warning('Email failed to send — send reliance letter manually.');
      } else if (data?.success === false) {
        // Business negative (200) — structured error from server
        log.error('Clarifii reliance send rejected', { loanId: loan.id, errorCode: data.errorCode });
        toast.warning(data.error || 'Email failed to send — send reliance letter manually.');
      } else if (data?.auditWarning) {
        toast.warning(data.auditWarning);
      } else {
        toast.success('Clarifi cleared — reliance letter sent');
      }
    } catch (err) {
      log.error('Error invoking send-clarifii-reliance', { loanId: loan.id }, err as Error);
      toast.warning('Email failed to send — send reliance letter manually.');
    } finally {
      setSendingClarifi(false);
      setClarifiConfirmOpen(false);
      setPendingClarifiEmail(null);
    }
  }, [loan.id, loan.encompass_loan_id]);

  const handleSkipClarifiSend = useCallback(() => {
    setClarifiConfirmOpen(false);
    setPendingClarifiEmail(null);
    toast.info('Clarifi cleared date saved. Email not sent.');
  }, []);

  // ── Investor Conditions handlers ──────────────────────────────────────────

  const condStatus = tracking?.investor_conditions_status || 'none';

  const handleMarkConditionsOutstanding = useCallback(async () => {
    if (!user?.id || !loan.organization_id) {
return;
}

    try {
      setSavingConditions(true);
      const userName = (user as { fullName?: string }).fullName || user.email || 'Unknown';
      const now = new Date().toISOString();

      const { error } = await supabase
        .from('loan_post_closing_tracking')
        .upsert({
          organization_id: loan.organization_id,
          encompass_loan_id: loan.encompass_loan_id,
          investor_conditions_status: 'outstanding',
          conditions_outstanding_at: now,
          updated_by: user.id,
          updated_by_name: userName,
          updated_at: now
        }, { onConflict: 'organization_id,encompass_loan_id' });

      if (error) {
        log.error('Failed to mark conditions outstanding', { loanId: loan.id }, error as unknown as Error);
        toast.error('Failed to save conditions status');
        return;
      }

      setTracking({
        investor_conditions_status: 'outstanding',
        conditions_outstanding_at: now,
        conditions_cleared_at: null,
        updated_by_name: userName
      });
      onLoanUpdate?.({ investor_conditions_status: 'outstanding', conditions_outstanding_at: now });
      toast.success('Investor conditions marked as outstanding');
    } catch (err) {
      log.error('Error saving conditions status', {}, err as Error);
      toast.error('Failed to save conditions status');
    } finally {
      setSavingConditions(false);
    }
  }, [user, loan.id, loan.organization_id, loan.encompass_loan_id, onLoanUpdate]);

  const handleMarkConditionsCleared = useCallback(async () => {
    if (!user?.id || !loan.organization_id) {
return;
}

    try {
      setSavingConditions(true);
      const userName = (user as { fullName?: string }).fullName || user.email || 'Unknown';
      const now = new Date().toISOString();

      const { error } = await supabase
        .from('loan_post_closing_tracking')
        .upsert({
          organization_id: loan.organization_id,
          encompass_loan_id: loan.encompass_loan_id,
          investor_conditions_status: 'cleared',
          conditions_cleared_at: now,
          // Preserve conditions_outstanding_at — append-only
          conditions_outstanding_at: tracking?.conditions_outstanding_at || now,
          updated_by: user.id,
          updated_by_name: userName,
          updated_at: now
        }, { onConflict: 'organization_id,encompass_loan_id' });

      if (error) {
        log.error('Failed to mark conditions cleared', { loanId: loan.id }, error as unknown as Error);
        toast.error('Failed to save conditions status');
        return;
      }

      setTracking(prev => ({
        investor_conditions_status: 'cleared',
        conditions_outstanding_at: prev?.conditions_outstanding_at || now,
        conditions_cleared_at: now,
        updated_by_name: userName
      }));
      onLoanUpdate?.({ investor_conditions_status: 'cleared', conditions_cleared_at: now });
      toast.success('Investor conditions marked as cleared');
    } catch (err) {
      log.error('Error saving conditions status', {}, err as Error);
      toast.error('Failed to save conditions status');
    } finally {
      setSavingConditions(false);
    }
  }, [user, loan.id, loan.organization_id, loan.encompass_loan_id, tracking?.conditions_outstanding_at, onLoanUpdate]);

  // ── PA upload handler ───────────────────────────────────────────────────

  const handlePAUpload = useCallback(async (file: File) => {
    if (!user?.id || !loan.organization_id) {
return;
}

    try {
      setUploadingPA(true);
      const hadPurchaseAdviceBeforeUpload = Boolean(paDoc || loan.first_pa_date);

      const result = await uploadPurchaseAdvice({
        file,
        organizationId: loan.organization_id,
        encompassLoanId: loan.encompass_loan_id,
        userId: user.id,
        uploadContext: 'post_closing',
        bankerReconId: bankerRecon?.id || null
      });

      setPaDoc({
        id: result.docId,
        source_file: result.storagePath,
        created_at: result.createdAt,
        uploaded_by: user.id
      });

      // Only set first_pa_date if no earlier PA exists
      if (!loan.first_pa_date) {
        onLoanUpdate?.({ first_pa_date: result.createdAt });
      }

      toast.success('Purchase Advice uploaded');

      if (hadPurchaseAdviceBeforeUpload) {
        log.info('Skipping Loan Purchased email for PA re-upload', {
          loanId: loan.id,
          encompassLoanId: loan.encompass_loan_id,
          loanDocumentId: result.docId
        });
        return;
      }

      void sendPostClosingLoanPurchasedEmail({
        encompassLoanId: loan.encompass_loan_id,
        loanDocumentId: result.docId
      }).then((emailResult) => {
        if (emailResult.status !== 'error') {
          return;
        }

        log.warn('Purchase Advice uploaded, but Loan Purchased email was not sent', {
          loanId: loan.id,
          encompassLoanId: loan.encompass_loan_id,
          loanDocumentId: result.docId,
          reason: emailResult.reason
        });
        toast.warning('Purchase Advice uploaded, email not sent');
      }).catch((emailError) => {
        log.warn('Purchase Advice uploaded, but Loan Purchased email request failed', {
          loanId: loan.id,
          encompassLoanId: loan.encompass_loan_id,
          loanDocumentId: result.docId
        }, emailError as Error);
        toast.warning('Purchase Advice uploaded, email not sent');
      });
    } catch (err) {
      log.error('Failed to upload PA', { loanId: loan.id }, err as Error);
      toast.error('Failed to upload Purchase Advice');
    } finally {
      setUploadingPA(false);
    }
  }, [user, loan.id, loan.organization_id, loan.encompass_loan_id, loan.first_pa_date, paDoc, bankerRecon?.id, onLoanUpdate]);

  const handleViewPA = useCallback(async () => {
    if (!paDoc?.source_file) {
return;
}
    try {
      const url = await getDocumentDownloadUrl(paDoc.source_file);
      window.open(url, '_blank');
    } catch {
      toast.error('Failed to open document');
    }
  }, [paDoc?.source_file]);

  // ── Post Closing comments and condition uploads ─────────────────────────

  const handleAddPostClosingComment = useCallback(async () => {
    const content = commentText.trim();
    if (!content) {
return;
}

    try {
      setAddingComment(true);
      const comment = await addPostClosingComment(loan.encompass_loan_id, content);
      const nextCount = postClosingComments.length + 1;
      setPostClosingComments(prev => [...prev, comment]);
      setCommentText('');
      onLoanUpdate?.({
        post_closing_comments_count: nextCount,
        post_closing_latest_comment_at: comment.created_at,
        post_closing_latest_comment_author_name: comment.author_name,
        post_closing_latest_comment_preview: compactCommentPreview(comment.content)
      });
      toast.success('Comment added');
    } catch (err) {
      log.error('Failed to add Post Closing comment', { loanId: loan.id }, err as Error);
      toast.error('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  }, [commentText, loan.encompass_loan_id, loan.id, onLoanUpdate, postClosingComments.length]);

  const handleConditionUpload = useCallback(async (file: File) => {
    try {
      setUploadingCondition(true);
      await uploadPostClosingCondition({
        loanId: loan.id,
        encompassLoanId: loan.encompass_loan_id,
        file
      });
      await refreshConditionDocs();
      toast.success('Post Closing condition queued');
    } catch (err) {
      log.error('Failed to upload Post Closing condition', { loanId: loan.id }, err as Error);
      await refreshConditionDocs().catch((refreshError) => {
        log.error('Failed to refresh Post Closing condition documents after upload error', { loanId: loan.id }, refreshError as Error);
      });
      toast.error(err instanceof Error ? err.message : 'Failed to upload condition');
    } finally {
      setUploadingCondition(false);
    }
  }, [loan.id, loan.encompass_loan_id, refreshConditionDocs]);

  const handleViewConditionDoc = useCallback(async (storagePath: string | null) => {
    if (!storagePath) {
return;
}
    try {
      const url = await getDocumentDownloadUrl(storagePath);
      window.open(url, '_blank');
    } catch {
      toast.error('Failed to open document');
    }
  }, []);

  // Build a merged loan object that overlays fresher local state onto the parent RPC snapshot
  const stepperLoan = useMemo(() => ({
    ...loan,
    ...(tracking ? {
      investor_conditions_status: tracking.investor_conditions_status,
      conditions_outstanding_at: tracking.conditions_outstanding_at || undefined,
      conditions_cleared_at: tracking.conditions_cleared_at || undefined
    } : {}),
    ...(paDoc ? { first_pa_date: paDoc.created_at } : {}),
    ...(bankerRecon ? {
      recon_status: bankerRecon.status,
      recon_finalized_at: bankerRecon.finalized_at || undefined
    } : {})
  }), [loan, tracking, paDoc, bankerRecon]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <>
    <div className="space-y-6">
      {/* Delivery Progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Delivery Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <PostClosingProgress loan={stepperLoan} variant="expanded" />
        </CardContent>
      </Card>

      {/* Buy Price Tracking Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Buy Price Tracking
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Revenue breakdown like Net Revenue card */}
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Revenue</div>

          {/* Encompass Buy Price */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Encompass Buy Price</span>
            <span className="text-sm font-mono">
              {formatPoints(loan.rate_lock_total_buy_price || 0, 4)}
            </span>
          </div>

          {/* Margin breakdown table */}
          {marginAdjustments.length > 0 && (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent bg-muted/50">
                    <TableHead className="text-xs h-8">Margin</TableHead>
                    <TableHead className="text-xs h-8 text-right w-20">Points</TableHead>
                    <TableHead className="text-xs h-8 text-right w-24">Dollars</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {marginAdjustments.map((adj, idx) => {
                    const displayCategory = getDisplayCategory(adj.description, pricingAdjustments);
                    const points = Math.abs(adj.rate);
                    const dollars = bpsToDollars(points * 100, loanAmount);
                    return (
                      <TableRow key={idx} className="hover:bg-muted/30">
                        <TableCell className="text-xs py-2">{displayCategory}</TableCell>
                        <TableCell className="text-xs py-2 text-right font-mono text-green-600">
                          +{formatPoints(points, 4)}
                        </TableCell>
                        <TableCell className="text-xs py-2 text-right font-mono text-green-600">
                          {formatCurrency(dollars)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {marginAdjustments.length > 1 && (
                  <TableFooter>
                    <TableRow className="bg-muted/30">
                      <TableCell className="text-xs py-2 font-medium">Total Margin</TableCell>
                      <TableCell className="text-xs py-2 text-right font-mono font-medium text-green-600">
                        +{formatPoints(totalMarginPoints, 4)}
                      </TableCell>
                      <TableCell className="text-xs py-2 text-right font-mono font-medium text-green-600">
                        {formatCurrency(totalMarginDollars)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </div>
          )}

          {/* Investor Lock Price (total) */}
          <div className="flex justify-between items-center py-2 bg-muted/50 px-3 rounded-md">
            <div>
              <span className="text-sm font-semibold">Investor Lock Price</span>
              <span className="text-xs text-muted-foreground ml-2">
                ({formatPoints(pointsAbovePar, 3)}% above par)
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono font-semibold">
                {investorLockPrice > 0 ? formatPoints(investorLockPrice, 4) : '-'}
              </span>
              <span className="text-sm font-mono font-semibold">
                {formatCurrency(grossRevenueDollars)}
              </span>
            </div>
          </div>

          {/* Link to full Banker Recon */}
          {bankerRecon && (
            <p className="text-xs text-muted-foreground">
              Full reconciliation available in{' '}
              <a href="/recon/banker" className="text-primary hover:underline inline-flex items-center gap-1">
                Banker Reconciliation
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delivery Events Card */}
      <Card role="region" aria-label="Delivery events">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            Delivery Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Funded Date + Ready to Ship */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Funded Date</div>
                <div className="text-sm font-medium mt-1">
                  {loan.funded_date ? formatDate(loan.funded_date) : '-'}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Ready to Ship</div>
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Input
                          type="date"
                          value={readyToShipDate.value}
                          onChange={(e) => readyToShipDate.setValue(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </TooltipTrigger>
                      {loan.ready_to_ship_set_by_name && loan.ready_to_ship_set_at && (
                        <TooltipContent side="bottom">
                          <p>Set by {loan.ready_to_ship_set_by_name}</p>
                          <p>{new Date(loan.ready_to_ship_set_at).toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  {readyToShipDate.isDirty && (
                    <Button
                      size="sm"
                      onClick={handleSaveReadyToShip}
                      disabled={savingReadyToShip}
                      className="h-8 px-2"
                    >
                      {savingReadyToShip ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Collateral Ship Date + Tracking */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                  Collateral Ship Date
                  <SyncStatusIcon status={shipDateSyncStatus.status} error={shipDateSyncStatus.error} />
                </div>
                <Input
                  type="date"
                  value={shipDate.value}
                  onChange={(e) => handleShippingChange('shipDate', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                  Tracking Number
                  <SyncStatusIcon status={trackingNumberSyncStatus.status} error={trackingNumberSyncStatus.error} />
                </div>
                <Input
                  type="text"
                  value={trackingNumber.value}
                  onChange={(e) => handleShippingChange('trackingNumber', e.target.value)}
                  placeholder="Enter tracking number"
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* Investor Package Delivery Date */}
            <div className={cn('grid grid-cols-1 items-end gap-4 sm:grid-cols-2', isPrimis && 'lg:grid-cols-3')}>
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-2">
                  Investor Package Delivery Date
                  <SyncStatusIcon status={investorDeliverySyncStatus.status} error={investorDeliverySyncStatus.error} />
                  {lockMet && (
                    <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                      <CheckCircle2 className="h-3 w-3" />
                      Lock Met
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={investorDeliveryDate.value}
                    onChange={(e) => handleShippingChange('investorDeliveryDate', e.target.value)}
                    className="h-8 text-sm max-w-[200px]"
                  />
                  {loan.rate_lock_buy_side_lock_expires && (
                    <span className="text-xs text-muted-foreground">
                      Lock expires: {formatDate(loan.rate_lock_buy_side_lock_expires)}
                    </span>
                  )}
                </div>
              </div>
              {warehouseEnabled && <WarehouseLineSelect control={warehouse} draft disabled={isSavingShipping} />}
              {isPrimis && <PrimisCollateralDate key={loan.id} control={collateral} disabled={isSavingShipping} />}
            </div>

            {/* Clarifi Cleared Date — shown only for Clarifii-enabled investors */}
            {isClarifiiLoan && (
              <>
                <Separator />
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Clarifi Cleared Date
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={clarifiClearedDate.value}
                      onChange={(e) => clarifiClearedDate.setValue(e.target.value)}
                      className="h-8 text-sm max-w-[200px]"
                    />
                    {clarifiClearedDate.isDirty && (
                      <Button
                        size="sm"
                        onClick={handleSaveClarifiCleared}
                        disabled={savingClarifiCleared}
                        className="h-8"
                      >
                        {savingClarifiCleared ? (
                          <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving...</>
                        ) : (
                          <><Save className="h-3 w-3 mr-1" /> Save</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Save Button */}
            {shippingDirty && (
              <div className="flex justify-end pt-2">
                <Button
                  size="sm"
                  onClick={handleSaveShipping}
                  disabled={isSavingShipping || (!shippingFieldsDirty && !warehouseReadyToSave && !collateralReadyToSave)}
                  className="h-8"
                >
                  {isSavingShipping ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Delivery Events'
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Investor Conditions Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Investor Conditions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {condStatus === 'none' && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  No investor conditions reported
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMarkConditionsOutstanding}
                  disabled={savingConditions}
                  className="h-8"
                >
                  {savingConditions ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : null}
                  Mark Conditions Outstanding
                </Button>
              </div>
            )}

            {condStatus === 'outstanding' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-900">Conditions Outstanding</p>
                    {tracking?.conditions_outstanding_at && (
                      <p className="text-xs text-yellow-700">
                        Since {formatDate(tracking.conditions_outstanding_at)}
                        {tracking.updated_by_name ? ` by ${tracking.updated_by_name}` : ''}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={handleMarkConditionsCleared}
                    disabled={savingConditions}
                    className="h-8 bg-green-600 hover:bg-green-700"
                  >
                    {savingConditions ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                    )}
                    Mark Cleared
                  </Button>
                </div>
              </div>
            )}

            {condStatus === 'cleared' && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-900">Conditions Cleared</p>
                  <p className="text-xs text-green-700">
                    {tracking?.conditions_outstanding_at && (
                      <>Outstanding since {formatDate(tracking.conditions_outstanding_at)}</>
                    )}
                    {tracking?.conditions_cleared_at && (
                      <> &middot; Cleared {formatDate(tracking.conditions_cleared_at)}</>
                    )}
                    {tracking?.updated_by_name && (
                      <> by {tracking.updated_by_name}</>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Post Closing Comments Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Post Closing Comments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-h-64 overflow-y-auto space-y-3 rounded-md border bg-muted/20 p-3">
            {postClosingComments.length > 0 ? (
              postClosingComments.map((comment) => (
                <div key={comment.id} className="border-b border-border/60 pb-3 last:border-0 last:pb-0">
                  <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-sm font-medium">{comment.author_name || 'Unknown User'}</span>
                    <span className="text-xs text-muted-foreground">{formatDateTime(comment.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
                    {comment.content}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No Post Closing comments yet</p>
            )}
          </div>
          <div className="space-y-2">
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a Post Closing comment"
              className="min-h-[84px] resize-y"
              disabled={addingComment}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleAddPostClosingComment}
                disabled={addingComment || !commentText.trim()}
                className="h-8"
              >
                {addingComment ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <MessageSquare className="h-3 w-3 mr-1" />
                )}
                Add Comment
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Post Closing Conditions Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Post Closing Conditions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {conditionDocs.length > 0
                ? `${conditionDocs.length} condition document${conditionDocs.length === 1 ? '' : 's'}`
                : 'No condition documents uploaded'}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => conditionInputRef.current?.click()}
              disabled={uploadingCondition}
              className="h-8"
            >
              {uploadingCondition ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Upload className="h-3 w-3 mr-1" />
              )}
              Upload PDF
            </Button>
          </div>

          {conditionDocs.length > 0 && (
            <div className="space-y-2">
              {conditionDocs.map((doc) => {
                const status = getConditionStatus(doc);
                const statusView = conditionStatusView(status);
                const StatusIcon = statusView.icon;
                const errorMessage = getConditionError(doc);

                return (
                  <div key={doc.document_id} className="rounded-md border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                          <span className="truncate text-sm font-medium">
                            {doc.original_filename || doc.file_name || 'Post Closing Condition.pdf'}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Uploaded {formatDateTime(doc.created_at)}
                          {doc.completed_at ? ` · Completed ${formatDateTime(doc.completed_at)}` : ''}
                        </p>
                        {errorMessage && (
                          <p className="mt-1 break-words text-xs text-red-600">{errorMessage}</p>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium', statusView.className)}>
                          <StatusIcon className={cn('h-3 w-3', status === 'processing' && 'animate-spin')} />
                          {statusView.label}
                        </span>
                        {doc.source_file && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewConditionDoc(doc.source_file)}
                            className="h-8 px-2"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <input
            ref={conditionInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
handleConditionUpload(file);
}
              e.target.value = '';
            }}
          />
        </CardContent>
      </Card>

      {/* Purchase Advice Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Purchase Advice
          </CardTitle>
        </CardHeader>
        <CardContent>
          {uploadingPA ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </div>
          ) : paDoc ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-sm font-medium">PA Received</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(paDoc.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleViewPA}
                  className="h-8"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => paInputRef.current?.click()}
                  className="h-8"
                >
                  <Upload className="h-3 w-3 mr-1" />
                  Re-upload
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">No Purchase Advice uploaded</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => paInputRef.current?.click()}
                className="h-8"
              >
                <Upload className="h-3 w-3 mr-1" />
                Upload PA
              </Button>
            </div>
          )}
          <input
            ref={paInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
handlePAUpload(file);
}
              e.target.value = '';
            }}
          />
          {bankerRecon && paDoc && (
            <p className="text-xs text-muted-foreground mt-2">
              Linked to banker recon record
            </p>
          )}
        </CardContent>
      </Card>

    </div>

    {/* Clarifii Reliance Letter Confirmation Dialog */}
    <AlertDialog open={clarifiConfirmOpen} onOpenChange={(open) => {
      if (!open && !sendingClarifi) {
handleSkipClarifiSend();
}
    }}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Send Clarifii Reliance Letter?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>A reliance letter request will be sent to Clarifii for this loan.</p>
              {pendingClarifiEmail && (
                <div className="bg-muted/50 rounded-md p-3 text-xs space-y-2 border">
                  <div><span className="font-medium text-foreground">To:</span> {pendingClarifiEmail.to.join(', ')}</div>
                  <div><span className="font-medium text-foreground">CC:</span> {pendingClarifiEmail.cc.join(', ')}</div>
                  <div><span className="font-medium text-foreground">Subject:</span> {pendingClarifiEmail.subject}</div>
                  <div className="pt-1 border-t whitespace-pre-line text-muted-foreground">
                    {pendingClarifiEmail.body}
                  </div>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={sendingClarifi}>
            Don&apos;t Send
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirmClarifiSend();
            }}
            disabled={sendingClarifi}
          >
            {sendingClarifi ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
            ) : (
              'Send'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// Loading skeleton
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

InvestorDeliveryTab.displayName = 'InvestorDeliveryTab';
